import Vue from 'vue';
import Router from 'vue-router';
import { BASE_URL } from '@/assets/js/config';
import { createOrUpdateUser } from '@/assets/js/api/current-user';
import { storeInvitation } from '@/assets/js/invitation';
import { handleAuthentication, requireAuth } from '@/assets/js/auth';
import PosthogPlugin from '../plugins/posthog'
import posthog from 'posthog-js'

const fCanny = () => import('@/views/f-canny');
const fClean = () => import('@/views/account/f-clean');

// Overviews
const fInstances = () => import('@/views/openstack/f-instances');
const fInstanceCreate = () => import('@/views/openstack/f-instance-create');
const fInstanceDetails = () => import('@/views/openstack/f-instance-details');
const fInstanceDetailsPorts = () => import('@/views/openstack/f-instance-details-ports');
const instanceActions = () => import('@/views/openstack/f-instance-details-actions');

// Volume Store
const fVolumeStore = () => import('@/views/openstack/f-volume-store');
const fVolumeDetails = () => import('@/views/openstack/f-volume-details');

const fVolumeBackup = () => import('@/views/openstack/f-volume-backup');
const fVolumeBackupDetails = () => import('@/views/openstack/f-volume-details');

// Images
const fImages = () => import('@/views/openstack/f-images');
const fImage = () => import('@/views/openstack/f-image-details');

// Images
const fSnapshots = () => import('@/views/openstack/f-snapshots');
const fSnapshot = () => import('@/views/openstack/f-snapshot-details');

const fIps = () => import('@/views/openstack/f-ips');
const fKeyPairs = () => import('@/views/openstack/f-key-pairs');
// const fOrchestration = () => import('@/views/openstack/f-orchestration');

// Networks
const fNetworks = () => import('@/views/openstack/f-networks');
const fNetwork = () => import('@/views/openstack/f-network-details');
const fNetworkPorts = () => import('@/views/openstack/f-network-details-ports');
const fNetworkSubnets = () => import('@/views/openstack/f-network-details-subnets');
const fNetworkTopo = () => import('@/views/openstack/f-network-topo');

const fPorts = () => import('@/views/openstack/f-ports');
const fPort = () => import('@/views/openstack/f-port-details');

const fRouters = () => import('@/views/openstack/f-routers');
const fRouter = () => import('@/views/openstack/f-router-details');

// Security Groups
const fSecurityGroups = () => import('@/views/openstack/f-security-groups');
const fSecurityGroupRules = () => import('@/views/openstack/f-security-group-rules');

// Object Store
const fObjectStore = () => import('@/views/openstack/f-object-store');
const fContainer = () => import('@/views/openstack/f-container');

// Load Balancer
const fLoadBalancers = () => import('@/views/openstack/f-load-balancers');
const fLoadBalancerDetails = () => import('@/views/openstack/f-load-balancer-details');
const fLoadBalancerCreate = () => import('@/views/openstack/f-load-balancer-create');

// EMK
const fPostEmk = () => import('@/views/emk/f-post-emk');
const fEmk = () => import('@/views/emk/f-emk');
const fEmkSecrets = () => import('@/views/emk/f-emk-secrets');
const fEmkServiceAccounts = () => import('@/views/emk/f-emk-service-accounts');
const fEmkCreate = () => import('@/views/emk/f-emk-create');
const fEmkDetails = () => import('@/views/emk/f-emk-details');
const fEmkYaml = () => import('@/views/emk/f-emk-details');
const fEmkWorkers = () => import('@/views/emk/f-emk-details');


// CLUSTERS
const fClusters = () => import('@/views/openstack/f-clusters');

// DNS
const fDns = () => import('@/views/openstack/f-dns');
const fDnsRecords = () => import('@/views/openstack/f-dns-records');
const fReverseDns = () => import('@/views/openstack/f-reverse-dns');

// DATABASES
const fDatabases = () => import('@/views/openstack/f-databases');
const fDatabaseDetails = () => import('@/views/openstack/f-database-details');

// BACKUPS
const fBackups = () => import('@/views/openstack/f-backups');

// File Store
const fFileStore = () => import('@/views/openstack/f-file-store');

// Containers
const fContainers = () => import('@/views/openstack/f-containers');

// GPU
const fGpu = () => import('@/views/openstack/f-gpu');

// General views
const fHome = () => import('@/views/f-home');
const fAcceptInvitation = () => import('@/views/account/f-accept-invitation');
const fLogout = () => import('@/views/f-logout');
const fCompleteAccount = () => import('@/views/f-complete-account');
const fChangePassword = () => import('@/views/f-change-password');
const fObjectStoreMigration = () => import('@/views/openstack/f-object-store-migration');
const fCysoManaged = () => import('@/views/f-cyso-managed');


// Account views
const fTeam = () => import('@/views/account/f-teams');
const fAccountUsers = () => import('@/views/account/f-account');
const fProfile = () => import('@/views/account/f-account');
const fActionTrail = () => import('@/views/account/f-account');
const fQuota = () => import('@/views/account/f-quota');

// Account - Access
const fAccess = () => import('@/views/access/f-access');
const fAccessCredentials = () => import('@/views/access/f-team-credentials');
const fApiEndpoints = () => import('@/views/access/f-api-endpoints');

const fCredentials = () => import('@/views/openstack/f-credentials');

// Billing views
const fBilling = () => import('@/views/f-billing');
const fBillingOverview = () => import('@/views/billing/f-billing-overview');
const fConfirmPaymentMethod = () => import('@/views/billing/f-confirm-payment-method');
const fInvoices = () => import('@/views/billing/f-invoices');
const fInvoiceSpecs = () => import('@/views/billing/f-invoice-specs');
const fUsage = () => import('@/views/billing/f-usage');

// Error views
const fError = () => import('@/views/f-error');
const fError404 = () => import('@/views/errors/f-404');
const fError500 = () => import('@/views/errors/f-500');
const fError502 = () => import('@/views/errors/f-502');
const fError503 = () => import('@/views/errors/f-503');
const fErrorAuth = () => import('@/views/errors/f-auth');

Vue.use(PosthogPlugin).use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '*',
      redirect: { name: '404' }
    },
    {
      path: '/error',
      name: 'error',
      component: fError,
      meta: {
        bodyClass: 'error-page no-header no-sidebar no-footer no-alerts',
        sidebar: 'error',
        access: 'all',
      },
      children: [
        {
          path: '/error/404',
          name: '404',
          component: fError404,
          meta: {
            title: '404 error - My Cyso | Cloud',
            breadcrumb: '404 error',
            sidebar: 'error',
            access: 'all',
          }
        },
        {
          path: '/error/500',
          name: '500',
          component: fError500,
          meta: {
            title: '500 error - My Cyso | Cloud',
            breadcrumb: '500 error',
            sidebar: 'error',
            access: 'all',
          }
        },
        {
          path: '/error/502',
          name: '502',
          component: fError502,
          meta: {
            title: '502 error - My Cyso | Cloud',
            breadcrumb: '502 error',
            sidebar: 'error',
            access: 'all',
          }
        },
        {
          path: '/error/503',
          name: '503',
          component: fError503,
          meta: {
            title: '503 error - My Cyso | Cloud',
            breadcrumb: '503 error',
            sidebar: 'error',
            access: 'all',
          }
        },
        {
          path: '/error/auth',
          name: 'auth',
          component: fErrorAuth,
          meta: {
            title: 'Auth error - My Cyso | Cloud',
            breadcrumb: 'Auth error',
            sidebar: 'error',
            access: 'all',
          }
        }
      ]
    },
    {
      path: '/',
      name: 'home',
      component: fHome,
      meta: {
        title: 'My Cyso | Cloud',
        breadcrumb: 'Home',
        sidebar: 'home',
      }
    },
    {
      path: '/canny',
      name: 'canny',
      component: fCanny,
      meta: {
        title: 'Canny SSO - My Cyso | Cloud',
        breadcrumb: 'canny',
        sidebar: 'canny',
        access: 'all',
      },
    },
    {
      path: '/instance',
      name: 'instances',
      redirect: {name: 'instance-overview'},
      component: fClean,
      meta: {
        title: 'Instances - My Cyso | Cloud',
        breadcrumb: 'Instances',
        sidebar: 'instance',
      },
      children: [
        {
          path: '/instances',
          name: 'instance-overview',
          component: fInstances,
          meta: {
            title: 'Instances - My Cyso | Cloud',
            breadcrumb: 'Instances',
            sidebar: 'instance',
          }
        },
        {
          path: '/instances/:region/:id',
          name: 'instance',
          component: fInstanceDetails,
          props: true,
          meta: {
            title: 'Instance - My Cyso | Cloud',
            breadcrumb: 'Instance',
            sidebar: 'instance',
          }
        },
        {
          path: '/instances/:region/:id/ports',
          name: 'instancePorts',
          component: fInstanceDetailsPorts,
          props: true,
          meta: {
            title: 'Instance ports - My Cyso | Cloud',
            breadcrumb: 'Instance',
            sidebar: 'instance',
          },
        },
        {
          path: '/instances/:region/:id/actions',
          name: 'instanceActions',
          component: instanceActions,
          props: true,
          meta: {
            title: 'Instance actions - My Cyso | Cloud',
            breadcrumb: 'Instance',
            sidebar: 'instance',
          },
        },
        {
          path: '/instance/create',
          name: 'instance-create',
          component: fInstanceCreate,
          meta: {
            title: 'Create instance - My Cyso | Cloud',
            breadcrumb: 'Create instance',
            sidebar: 'instance',
          }
        },
      ],
    },
    {
      path: '/object-store',
      name: 'object-store',
      redirect: { name: 'object-store-overview' },
      component: fClean,
      meta: {
        title: 'Object Store - My Cyso | Cloud',
        breadcrumb: 'Object Store',
        sidebar: 'object-store',
        access: 'objectStore',
      },
      children: [
        {
          path: '/object-store',
          name: 'object-store-overview',
          component: fObjectStore,
          meta: {
            title: 'Object Store - My Cyso | Cloud',
            breadcrumb: 'Object Store',
            sidebar: 'object-store',
            access: 'objectStore',
          },
        },
        {
          path: '/object-store/:region/:name/:path(.*)',
          props: true,
          name: 'container',
          component: fContainer,
          meta: {
            title: 'Object Store Container - My Cyso | Cloud',
            breadcrumb: 'Object Store Container',
            sidebar: 'object-store',
            access: 'objectStore',
          },
        },
      ],
    },
    {
      path: '/kubernetes-overview',
      name: 'managed-kubernetes-parent',
      redirect: { name: 'kubernetes' },
      component: fClean,
      meta: {
        title: 'Kubernetes - My Cyso | Cloud',
        breadcrumb: 'Kubernetes',
        sidebar: 'kubernetes',
      },
      children: [
        {
          path: '/managed-kubernetes',
          name: 'managed-kubernetes',
          component: fEmk,
          meta: {
            title: 'Kubernetes - My Cyso | Cloud',
            breadcrumb: 'Kubernetes',
            sidebar: 'emk',
          },
        },
        {
          path: '/post-emk',
          name: 'post-emk',
          component: fPostEmk,
          sidebar: 'emk',
        },
        {
          path: '/managed-kubernetes/create',
          name: 'emk-create',
          component: fEmkCreate,
          meta: {
            title: 'Kubernetes - Create Cluster - My Cyso | Cloud',
            breadcrumb: 'Kubernetes - Create Cluster',
            sidebar: 'emk',
          },
        },
        {
          path: '/managed-kubernetes/:id',
          name: 'managed-kubernetes-details',
          component: fEmkDetails,
          props: route => ({ worker: route.query.worker }),
          meta: {
            title: 'Kubernetes cluster - My Cyso | Cloud',
            breadcrumb: 'Kubernetes cluster details',
            sidebar: 'emk',
          },
        },
        {
          path: '/managed-kubernetes/:id/workers',
          name: 'emk-workers',
          component: fEmkWorkers,
          props: route => ({ worker: route.query.worker }),
          meta: {
            title: 'Workers Kubernetes cluster - My Cyso | Cloud',
            breadcrumb: 'Kubernetes cluster workers',
            sidebar: 'emk',
          },
        },
        {
          path: '/managed-kubernetes/:id/yaml',
          name: 'managed-kubernetes-yaml',
          component: fEmkYaml,
          props: route => ({ worker: route.query.worker }),
          meta: {
            title: 'Yaml Kubernetes cluster - My Cyso | Cloud',
            breadcrumb: 'Kubernetes cluster yaml',
            sidebar: 'emk',
          },
        },
        {
          path: '/emk-secrets',
          name: 'emk-secrets',
          component: fEmkSecrets,
          meta: {
            title: 'EMK Secrets - My Cyso | Cloud',
            breadcrumb: 'EMK Secrets',
            sidebar: 'gardenerPagesSecrets',
          },
        },
        {
          path: '/emk-service-accounts',
          name: 'emk-service-accounts',
          component: fEmkServiceAccounts,
          meta: {
            title: 'EMK service accounts - My Cyso | Cloud',
            breadcrumb: 'EMK service accounts',
            sidebar: 'gardenerPagesAccounts',
          },
        },
      ],
    },
    {
      path: '/clusters-overview',
      name: 'clusters-parent',
      redirect: { name: 'clusters' },
      component: fClean,
      meta: {
        title: 'Databases - My Cyso | Cloud',
        breadcrumb: 'Databases',
        sidebar: 'cluster',
      },
      children: [
        {
          path: '/clusters',
          name: 'clusters',
          component: fClusters,
          meta: {
            title: 'Clusters - My Cyso | Cloud',
            breadcrumb: 'Clusters',
            sidebar: 'cluster',
          }
        },
      ],
    },
    {
      path: '/object-store-migration',
      name: 'object-store-migration',
      component: fObjectStoreMigration,
      meta: {
        title: 'Object Store Migration - My Cyso | Cloud',
        breadcrumb: 'Object Store Migration',
        sidebar: 'object-store',
        access: 'objectStore',
      }
    },
    {
      path: '/volume-store',
      name: 'volume-store',
      redirect: { name: 'volume-store-overview' },
      component: fClean,
      meta: {
        title: 'Volume Store - My Cyso | Cloud',
        breadcrumb: 'Volume Store',
        sidebar: 'volume-store',
      },
      children: [
        {
          path: '/volume-store',
          name: 'volume-store-overview',
          component: fVolumeStore,
          meta: {
            title: 'Volume Store - My Cyso | Cloud',
            breadcrumb: 'Volume Store',
            sidebar: 'volume-store',
          },
        },
        {
          path: '/volume-store/:region/:id',
          name: 'volume',
          component: fVolumeDetails,
          props: true,
          meta: {
            title: 'Volume Store volume - My Cyso | Cloud',
            breadcrumb: 'Volume Store volume',
            sidebar: 'volume-store',
          },
        },
      ],
    },
    {
      path: '/volume-backup',
      name: 'volume-backup',
      redirect: { name: 'volume-backup-overview' },
      component: fClean,
      meta: {
        title: 'Volume backup - My Cyso | Cloud',
        breadcrumb: 'Volume Store',
        sidebar: 'volume-store',
      },
      children: [
        {
          path: '/volume-backup',
          name: 'volume-backup-overview',
          component: fVolumeBackup,
          meta: {
            title: 'Volume backup - My Cyso | Cloud',
            breadcrumb: 'Volume backup',
            sidebar: 'volume-backup',
          },
        },
        {
          path: '/volume-backup/:id',
          name: 'volumebackup',
          component: fVolumeBackupDetails,
          props: true,
          meta: {
            title: 'Volume Store backup - My Cyso | Cloud',
            breadcrumb: 'Volume Store backup',
            sidebar: 'volume-backup',
          },
        },
      ],
    },
    {
      path: '/images-overview',
      name: 'images-parent',
      redirect: { name: 'images' },
      component: fClean,
      meta: {
        title: 'Images - My Cyso | Cloud',
        breadcrumb: 'Images',
        sidebar: 'images',
      },
      children: [
        {
          path: '/images',
          name: 'images',
          component: fImages,
          meta: {
            title: 'Images - My Cyso | Cloud',
            breadcrumb: 'Images',
            sidebar: 'images',
          },
        },
        {
          path: '/images/:region/:id',
          name: 'image',
          component: fImage,
          props: true,
          meta: {
            title: 'Image - My Cyso | Cloud',
            breadcrumb: 'Image',
            sidebar: 'images',
          },
        },
      ],
    },
    {
      path: '/snapshot-overview',
      name: 'snapshot-parent',
      redirect: { name: 'snapshots' },
      component: fClean,
      meta: {
        title: 'Images - My Cyso | Cloud',
        breadcrumb: 'Snapshots',
        sidebar: 'snapshot',
      },
      children: [
        {
          path: '/snapshots',
          name: 'snapshots',
          component: fSnapshots,
          meta: {
            title: 'Snapshots - My Cyso | Cloud',
            breadcrumb: 'Snapshots',
            sidebar: 'snapshot',
          },
        },
        {
          path: '/snapshots/:region/:id',
          name: 'snapshot',
          component: fSnapshot,
          props: true,
          meta: {
            title: 'Snapshot - My Cyso | Cloud',
            breadcrumb: 'Snapshot',
            sidebar: 'snapshot',
          },
        },
      ],
    },
    {
      path: '/ip-addresses',
      name: 'ip-addresses',
      component: fIps,
      meta: {
        title: 'IP Adresses - My Cyso | Cloud',
        breadcrumb: 'IP Adresses',
        sidebar: 'ip-addresses',
      }
    },
    {
      path: '/key-pairs',
      name: 'key-pairs',
      component: fKeyPairs,
      meta: {
        title: 'Key Pairs - My Cyso | Cloud',
        breadcrumb: 'Key Pairs',
        sidebar: 'key-pairs',
      }
    },
    // {
    //   path: '/orchestration',
    //   name: 'orchestration',
    //   component: fOrchestration,
    //   meta: {
    //     title: 'Orchestration - My Cyso | Cloud',
    //     breadcrumb: 'Orchestration'
    //   }
    // },
    {
      path: '/networks-overview',
      name: 'networks-overview',
      redirect: { name: 'networks' },
      component: fClean,
      meta: {
        title: 'Networks - My Cyso | Cloud',
        breadcrumb: 'Networks',
        sidebar: 'networks',
      },
      children: [
        {
          path: '/networks',
          name: 'networks',
          component: fNetworks,
          meta: {
            title: 'Networks - My Cyso | Cloud',
            breadcrumb: 'Networks',
            sidebar: 'networks',
          }
        },
        {
          path: '/networks/:region/:id',
          name: 'network',
          component: fNetwork,
          props: true,
          meta: {
            title: 'Network - My Cyso | Cloud',
            breadcrumb: 'Network',
            sidebar: 'networks',
          },
        },
        {
          path: '/networks/:region/:id/subnets',
          name: 'networkSubnets',
          component: fNetworkSubnets,
          props: true,
          meta: {
            title: 'Network Subnets - My Cyso | Cloud',
            breadcrumb: 'Network',
            sidebar: 'networks',
          },
        },
        {
          path: '/networks/:region/:id/ports',
          name: 'networkPorts',
          component: fNetworkPorts,
          props: true,
          meta: {
            title: 'Network Ports - My Cyso | Cloud',
            breadcrumb: 'Network',
            sidebar: 'networks',
          },
        },
      ],
    },
    {
      path: '/network-topology',
      name: 'networkTopo',
      component: fNetworkTopo,
      meta: {
        title: 'Network topology - My Cyso | Cloud',
        breadcrumb: 'Network topology',
        sidebar: 'network-topo',
      },
    },
    {
      path: '/ports',
      name: 'ports',
      component: fPorts,
      props: true,
      meta: {
        title: 'Ports - My Cyso | Cloud',
        breadcrumb: 'Ports',
        sidebar: 'port',
      },
    },
    {
      path: '/ports/:region/:id',
      name: 'port',
      component: fPort,
      props: true,
      meta: {
        title: 'Port - My Cyso | Cloud',
        breadcrumb: 'Port',
        sidebar: 'port',
      },
    },
    {
      path: '/routers',
      name: 'routers',
      component: fRouters,
      props: true,
      meta: {
        title: 'Routers - My Cyso | Cloud',
        breadcrumb: 'Routers',
        sidebar: 'router',
      },
    },
    {
      path: '/routers/:region/:id',
      name: 'router',
      component: fRouter,
      props: true,
      meta: {
        title: 'Router - My Cyso | Cloud',
        breadcrumb: 'Router',
        sidebar: 'router',
      },
    },
    {
      path: '/security-groups-overview',
      name: 'security-groups-parent',
      redirect: { name: 'security-groups-overview' },
      component: fClean,
      meta: {
        title: 'Security Groups - My Cyso | Cloud',
        breadcrumb: 'Security Groups',
        sidebar: 'security-groups',
      },
      children: [
        {
          path: '/security-groups',
          name: 'security-groups',
          component: fSecurityGroups,
          meta: {
            title: 'Security Groups - My Cyso | Cloud',
            breadcrumb: 'Security Groups',
            sidebar: 'security-groups',
          },
        },
        {
          path: '/security-groups/:id',
          name: 'security-group-rules',
          component: fSecurityGroupRules,
          props: true,
          meta: {
            title: 'Security Group Rules - My Cyso | Cloud',
            breadcrumb: 'Security Group Rules',
            sidebar: 'security-groups',
          },
        },
      ],
    },
    {
      path: '/load-balancers',
      name: 'load-balancers-overview',
      redirect: { name: 'load-balancers' },
      component: fClean,
      meta: {
        title: 'Load Balancers - My Cyso | Cloud',
        breadcrumb: 'Load Balancers',
        sidebar: 'load-balancer',
      },
      children: [
        {
          path: '/load-balancers',
          name: 'load-balancers',
          component: fLoadBalancers,
          meta: {
            title: 'Load Balancers - My Cyso | Cloud',
            breadcrumb: 'Load Balancers',
            sidebar: 'load-balancer',
          }
        },
        {
          path: '/load-balancers/create',
          name: 'load-balancer-create',
          component: fLoadBalancerCreate,
          meta: {
            title: 'Create Load Balancer - My Cyso | Cloud',
            breadcrumb: 'Create Load Balancer',
            sidebar: 'load-balancer',
          }
        },
        {
          path: '/load-balancers/:id',
          name: 'load-balancer',
          component: fLoadBalancerDetails,
          props: true,
          meta: {
            title: 'Load Balancer - My Cyso | Cloud',
            breadcrumb: 'Load Balancer',
            sidebar: 'load-balancer',
          }
        },
      ],
    },
    {
      path: '/dns',
      name: 'dns-parent',
      redirect: { name: 'dns' },
      component: fClean,
      meta: {
        title: 'DNS - My Cyso | Cloud',
        breadcrumb: 'DNS',
        sidebar: 'dns',
      },
      children: [
        {
          path: '/dns',
          name: 'dns',
          component: fDns,
          meta: {
            title: 'DNS as a Service - My Cyso | Cloud',
            breadcrumb: 'DNS',
            sidebar: 'dns',
          }
        },
        {
          path: '/dns/:id',
          name: 'dns-records',
          component: fDnsRecords,
          props: true,
          meta: {
            title: 'DNS Records - My Cyso | Cloud',
            breadcrumb: 'DNS Records',
            sidebar: 'dns',
          },
        },
        {
          path: '/reverse-dns',
          name: 'reverse-dns',
          component: fReverseDns,
          meta: {
            title: 'Reverse DNS - My Cyso | Cloud',
            breadcrumb: 'Reverse DNS',
            sidebar: 'dns',
          },
        },
      ],
    },
    {
      path: '/databases',
      name: 'databases-parent',
      redirect: { name: 'databases' },
      component: fClean,
      meta: {
        title: 'Databases - My Cyso | Cloud',
        breadcrumb: 'Databases',
        sidebar: 'databases',
      },
      children: [
        {
          path: '/databases',
          name: 'databases',
          component: fDatabases,
          meta: {
            title: 'Databases as a Service - My Cyso | Cloud',
            breadcrumb: 'Databases',
            sidebar: 'databases',
          }
        },
        {
          path: '/databases/:id',
          name: 'database',
          component: fDatabaseDetails,
          props: true,
          meta: {
            title: 'Database - My Cyso | Cloud',
            breadcrumb: 'Database',
            sidebar: 'databases',
          }
        },
        {
          path: '/databases/create',
          name: 'database-create',
          component: fInstanceCreate,
          meta: {
            title: 'Create database - My Cyso | Cloud',
            breadcrumb: 'Create database',
            sidebar: 'databases',
          }
        },
      ],
    },
    {
      path: '/file-store',
      name: 'file-store-parent',
      redirect: { name: 'file-store' },
      component: fClean,
      meta: {
        title: 'File Store - My Cyso | Cloud',
        breadcrumb: 'File Store',
        sidebar: 'file-store',
      },
      children: [
        {
          path: '/file-store',
          name: 'file-store',
          component: fFileStore,
          meta: {
            title: 'File Store - My Cyso | Cloud',
            breadcrumb: 'File Store',
            sidebar: 'file-store',
          }
        },
      ],
    },
    {
      path: '/containers',
      name: 'containers-parent',
      redirect: { name: 'containers' },
      component: fClean,
      meta: {
        title: 'Containers - My Cyso | Cloud',
        breadcrumb: 'Containers',
        sidebar: 'container',
      },
      children: [
        {
          path: '/containers',
          name: 'containers',
          component: fContainers,
          meta: {
            title: 'Containers - My Cyso | Cloud',
            breadcrumb: 'Containers',
            sidebar: 'container',
          }
        },
      ],
    },
    {
      path: '/backups',
      name: 'backups-parent',
      redirect: { name: 'backups' },
      component: fClean,
      meta: {
        title: 'Backups - My Cyso | Cloud',
        breadcrumb: 'Backups',
        sidebar: 'backups',
      },
      children: [
        {
          path: '/backups',
          name: 'backups',
          component: fBackups,
          meta: {
            title: 'Backups - My Cyso | Cloud',
            breadcrumb: 'Backups',
            sidebar: 'backups',
          }
        },
      ],
    },
    {
      path: '/complete-account',
      name: 'complete-account',
      component: fCompleteAccount,
      meta: {
        title: 'Complete account - My Cyso | Cloud',
        breadcrumb: 'Complete account',
        sidebar: 'complete-account',
        access: 'all',
      }
    },
    {
      path: '/pre-accept-invitation',
      name: 'pre-accept-invitation',
      meta: {
        title: 'Accept Invitation - My Cyso | Cloud',
        breadcrumb: 'Accept Invitation',
        sidebar: 'pre-accept-invitation',
        access: 'all',
      },
      beforeEnter: (to, from, next) => {
        storeInvitation(to.query);
      }
    },
    {
      path: '/accept-invitation',
      name: 'accept-invitation',
      component: fAcceptInvitation,
      meta: {
        title: 'Accept Invitation - My Cyso | Cloud',
        breadcrumb: 'Accept Invitation',
        sidebar: 'accept-invitation',
        access: 'all',
      }
    },
    {
      path: '/account',
      name: 'account',
      redirect: { name: 'profile' },
      component: fClean,
      meta: {
        title: 'Account - My Cyso | Cloud',
        breadcrumb: 'Account',
        sidebar: 'account',
        access: 'all',
      },
      children: [
        {
          path: '/account/team',
          name: 'team',
          component: fTeam,
          meta: {
            title: 'Team - My Cyso | Cloud',
            breadcrumb: 'Team',
            sidebar: 'team',
            access: 'all',
          }
        },
        {
          path: '/account/credentials',
          name: 'credentials',
          component: fCredentials,
          meta: {
            title: 'Credentials - My Cyso | Cloud',
            breadcrumb: 'Credentials',
            sidebar: 'ec-credential',
            access: 'objectStore',
          }
        },
        {
          path: '/account/application-credentials',
          name: 'app-credentials',
          component: fCredentials,
          meta: {
            title: 'Application Credentials - My Cyso | Cloud',
            breadcrumb: 'Application Credentials',
            sidebar: 'app-credential',
          }
        },
        {
          path: '/account/access-rules',
          name: 'access-rules',
          component: fCredentials,
          meta: {
            title: 'Access Rules - My Cyso | Cloud',
            breadcrumb: 'Access Rules',
            sidebar: 'access-rules',
          }
        },
        {
          path: '/account/team-credentials',
          name: 'team-credentials',
          component: fCredentials,
          meta: {
            title: 'Team Credentials - My Cyso | Cloud',
            breadcrumb: 'Team Credentials',
            sidebar: 'team-credential',
          }
        },
        {
          path: '/account/profile',
          name: 'profile',
          component: fProfile,
          meta: {
            title: 'Profile - My Cyso | Cloud',
            breadcrumb: 'Profile',
            sidebar: 'team',
            access: 'all',
          }
        },
        {
          path: '/account/action-trail',
          name: 'action-trail',
          component: fActionTrail,
          meta: {
            title: 'Action Trail - My Cyso | Cloud',
            breadcrumb: 'Profile',
            sidebar: 'team',
            access: 'all',
          }
        },
        {
          path: '/account/users',
          name: 'account-users',
          component: fTeam,
          meta: {
            title: 'Users - My Cyso | Cloud',
            breadcrumb: 'Users',
            sidebar: 'team',
            access: 'all',
          }
        },
        {
          path: '/account/cancel',
          name: 'account-cancel',
          component: fAccountUsers,
          meta: {
            title: 'Users - My Cyso | Cloud',
            breadcrumb: 'Users',
            sidebar: 'team',
          }
        },
        {
          path: '/account/billing-entity',
          name: 'account-billing-entity',
          redirect: { name: 'billing' },
          component: fClean,
          meta: {
            title: 'Billing Entity - My Cyso | Cloud',
            breadcrumb: 'Billing Entity',
            sidebar: 'billing',
          }
        },
        {
          path: '/account/access',
          name: 'access',
          component: fAccess,
          redirect: { name: 'team-credentials' },
          meta: {
            title: 'Access - My Cyso | Cloud',
            breadcrumb: 'Access',
            sidebar: 'access',
          },
          children: [
            {
              path: '/account/access',
              name: 'access-credentials',
              component: fAccessCredentials,
              redirect: { name: 'team-credentials' },
              meta: {
                title: 'Access - My Cyso | Cloud',
                breadcrumb: 'Access',
                sidebar: 'access',
              }
            },
            {
              path: '/account/api-endpoints',
              name: 'api-endpoints',
              component: fApiEndpoints,
              meta: {
                title: 'API Endpoints - My Cyso | Cloud',
                breadcrumb: 'API Endpoints',
                sidebar: 'api-endpoint',
              }
            }
          ]
        },
        {
          path: '/account/billing',
          name: 'account-billing',
          redirect: { name: 'billing' },
          component: fBilling,
          meta: {
            title: 'Billing overview - My Cyso | Cloud',
            breadcrumb: 'Billing',
            sidebar: 'billing',
          },
          children: [
            {
              path: '/account/billing/',
              name: 'billing',
              component: fBillingOverview,
              meta: {
                title: 'Billing overview - My Cyso | Cloud',
                breadcrumb: 'Billing overview',
                sidebar: 'billing',
              }
            },
            {
              path: '/account/billing/payment-methods',
              name: 'payment-methods',
              redirect: { name: 'billing' },
              meta: {
                title: 'Payment methods - My Cyso | Cloud',
                breadcrumb: 'Payment methods',
                sidebar: 'billing',
              }
            },
            {
              path: '/account/billing/confirm-ideal',
              name: 'ideal',
              component: fConfirmPaymentMethod,
              meta: {
                title: 'My Cyso | Cloud',
                breadcrumb: 'Confirm iDeal',
                sidebar: 'billing',
              }
            },
            {
              path: '/account/billing/confirm-sofort',
              name: 'sofort',
              component: fConfirmPaymentMethod,
              meta: {
                title: 'My Cyso | Cloud',
                breadcrumb: 'Confirm Sofort',
                sidebar: 'billing',
              }
            },
            {
              path: '/account/billing/invoices',
              name: 'invoices',
              component: fInvoices,
              meta: {
                title: 'Invoices - My Cyso | Cloud',
                breadcrumb: 'Invoices',
                sidebar: 'billing',
              }
            },
            {
              path: '/account/billing/invoice-specs',
              name: 'invoice-specs',
              component: fInvoiceSpecs,
              meta: {
                title: 'Invoice specs - My Cyso | Cloud',
                breadcrumb: 'Invoice specs',
                sidebar: 'invoice',
              }
            },
            {
              path: '/account/quota',
              name: 'quota',
              component: fQuota,
              meta: {
                title: 'Quota - My Cyso | Cloud',
                breadcrumb: 'Quota',
                sidebar: 'quota',
              }
            },
          ]
        },
        {
          path: '/account/billing/usage',
          name: 'usage',
          component: fUsage,
          meta: {
            title: 'My Cyso | Cloud',
            breadcrumb: 'Usage',
            sidebar: 'usage',
          }
        },
      ]
    },
    {
      path: '/sign-up',
      alias: '/register',
      name: 'sign-up',
      component: fLogout,
      meta: {
        title: 'My Cyso | Cloud',
        access: 'all',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: fLogout,
      meta: {
        title: 'My Cyso | Cloud',
        access: 'all',
      },
    },
    {
      path: '/post-login',
      name: 'post-login',
      component: fHome,
      meta: {
        title: 'My Cyso | Cloud',
        access: 'all',
      },
      beforeEnter: (to, from, next) => {
        handleAuthentication(() => {
          createOrUpdateUser()
          .then(() => {
            let url = '';
            if (!localStorage.getItem('hasLoggedIn')) {
                localStorage.setItem('hasLoggedIn', true);
            }
            if (localStorage.getItem('current_url') !== null) {
              url = localStorage.getItem('current_url');
            } else {
              url = '/';
            }
            localStorage.setItem('current_url', url);
            setTimeout(() => {
              window.location = url;
            }, 500);
          });
        });
      }
    },
    {
      path: '/logout',
      name: 'logout',
      component: fLogout,
      meta: {
        title: 'Logout - My Cyso | Cloud',
        breadcrumb: 'Logout',
        access: 'all',
      }
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: fChangePassword,
      meta: {
        title: 'My Cyso | Cloud',
        access: 'all',
      },
    },
    {
      path: '/cyso-managed',
      name: 'cyso',
      component: fCysoManaged,
      meta: {
        title: 'Cyso Managed | Cloud',
        access: 'all',
      },
    },
  ],

  scrollBehavior (to, from, savedPosition) {
    let el = document.querySelector('.main');
    if (el !== null) {
      el.scrollTo(0 , 0);
    }
  }
});

router.beforeEach((to, from, next) => {
  let url = '';
  if (to.meta.title !== undefined) {
    document.title = to.meta.title;
  } else {
    document.title = 'My Cyso | Cloud';
  }

  if (localStorage.getItem('current_url') !== null) {
    url = localStorage.getItem('current_url');
  }

  let serviceData = JSON.parse(localStorage.getItem('team_services'))
  if (!serviceData) {
    serviceData = []
  }

  posthog.capture('$pageleave')

  if(serviceData.indexOf('OBJECTSTORE') !== -1) {
       if (to.meta &&
          (to.meta.access === 'all') ||
          (to.meta.access === 'objectStore')) {
          next();
       } else {
          if (from !== '/object-store') {
            next('/object-store');
          }
       }
  }

  switch (to.name) {
    case 'home':
    // Auth pages
    case 'sign-up':
    case 'login':
    case 'post-login':
    case 'logout':
    case 'change-password':
    case 'canny':
    // Error pages
    case '404':
    case '500':
    case '502':
    case '503':
    case 'auth':
      url = '/';
      break;
    default:
      url = BASE_URL + to.path;
      localStorage.setItem('current_url', url);
  }

  router.afterEach(() => {
    Vue.nextTick(() => {
      posthog.capture('$pageview')
    })
  })

  requireAuth(to, from, next);
});


export default router;
